import { FunctionComponent, memo, useContext } from "react";
import { IFormValues, IProps } from "./anomaly-display-name-edit.types";
import { Button, FieldError } from "@edgetier/components";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { faPencil, faSave } from "@fortawesome/pro-solid-svg-icons";
import { Field, Form, Formik } from "formik";
import { IAnomaliesGetResponse, IAnomaly, Route } from "@edgetier/watchtower-types";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { InfiniteData, useQueryClient } from "react-query";
import { updateAnomaliesCache } from "./anomaly-display-name-edit.utilities";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import * as Yup from "yup";
import "./anomaly-display-name-edit.scss";
import { getTimeToRead } from "@edgetier/utilities";

/**
 * A button that opens a modal with a form that allows the user to edit an anomaly name.
 * @param props.anomalyId   The ID of the anomaly whose name is being edited.
 * @param props.displayName The current display name of the anomaly.
 */
const AnomalyDisplayNameEdit: FunctionComponent<IProps> = ({ anomalyId, displayName }) => {
    const queryClient = useQueryClient();
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });

    /**
     * Make the request to update the anomaly display name.
     * @param values.displayName The new display name for the anomaly.
     */
    const onSubmit = async (values: IFormValues) => {
        const displayName = values.displayName.trim();
        try {
            await axios.patch(Route.Anomaly.replace(":anomalyId", anomalyId), { displayName });
            queryClient.setQueryData<IAnomaly | undefined>([Route.Anomaly, anomalyId], (anomaly) => {
                if (typeof anomaly !== "undefined") {
                    return { ...anomaly, displayName };
                }
                return anomaly;
            });
            queryClient.setQueriesData<InfiniteData<IAnomaliesGetResponse> | undefined>(
                [Route.Anomalies, "anomaliesList"],
                updateAnomaliesCache(anomalyId, displayName)
            );
            const successMessage = `Anomaly renamed successfully`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            closeModal();
        } catch (error) {
            toast.error(getServerError(error));
        }
    };

    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Edit Anomaly Display Name</h2>
                </ModalHeader>
                <ModalContent>
                    <Formik<IFormValues>
                        initialValues={{ displayName }}
                        onSubmit={onSubmit}
                        validationSchema={Yup.object().shape({ displayName: Yup.string().trim().required("Required") })}
                    >
                        {({ isSubmitting }) => (
                            <Form>
                                <div className="field">
                                    <FieldError name="displayName" />
                                    <label htmlFor="displayName">Display Name</label>
                                    <Field
                                        autoFocus
                                        type="text"
                                        name="displayName"
                                        id="displayName"
                                        placeholder="Anomaly display name&hellip;"
                                    ></Field>
                                </div>
                                <Button type="submit" icon={faSave} isLoading={isSubmitting}>
                                    Submit
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </ModalContent>
            </Modal>
            <EllipsisMenuItem
                closeAfterClick={false}
                onClick={() => {
                    openModal();
                    lockOpen();
                }}
                icon={faPencil}
            >
                Rename
            </EllipsisMenuItem>
        </>
    );
};

export default memo(AnomalyDisplayNameEdit);
